import React from "react";
import { ProjectsStore } from "src/modules/common/stores";
import { TasksGridVisualStore, TasksFiltersVisualStore } from "../stores";
import { observer } from "mobx-react-lite";
import { CloseCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, DatePicker, Dropdown, Row, Tag } from "antd";
import { ItemType } from "antd/lib/menu/interface";
import { ColumnKeys, FilterOperator } from "./TaskList";
import MetadataFilterControls, {
    NumericFilterOptions,
} from "./MetadataFilterControls";
import {
    MetadataDefinition,
    MetadataFieldType,
} from "../../administration/types/Metadata";
import { ExtraDateFilter } from "../../administration/types/DashboardWidgetMeta";
import { Utils } from "../../common/misc/Utils";
import dayjs from "dayjs";
import { METADATA_CHANGES_FILTER_KEY } from "./FiltersControls";

type Props = {
    taskGridVisualStore: TasksGridVisualStore;
    filterStore: TasksFiltersVisualStore;
    projectsStore: ProjectsStore;
};

const LabelMapper = {
    status: "Status",
    assignedTo: "Assignee",
    createdBy: "Owner",
    priority: "Priority",
    project: "Project",
    taskType: "Type",
    createDate: "Created date",
    dueDate: "Due date",
} as { [key: string]: string };

const { RangePicker } = DatePicker;

const ComposedTableFilter: React.FC<Props> = ({
    taskGridVisualStore,
    // createStore,
    filterStore,
    // projectsStore,
}) => {
    const dropdownMenu = (
        items: ItemType[],
        label: string,
        overlayClassName = "",
    ) => {
        return (
            <Dropdown
                trigger={["click"]}
                menu={{ items }}
                overlayClassName={`alpha-dropdown-overlay composed-table-filter-menu ${overlayClassName}`}
            >
                <Button
                    className="dropdown-btn"
                    style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: 0,
                        borderRight: "none",
                    }}
                >
                    {label}
                </Button>
            </Dropdown>
        );
    };

    const getTaskTypeMenu = (ids: string[]) => {
        const items = filterStore.taskTypesForCurrentProject.map((t) => ({
            key: t.id,
            label: (
                <div
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent the menu from closing
                        filterStore.setCurrentFilterValue(
                            ColumnKeys.Type,
                            t.id,
                            false,
                        );
                    }}
                    data-testid={`task-type-${t.id}`}
                >
                    <Checkbox
                        style={{ marginRight: 10 }}
                        checked={filterStore.columnsFilterDict[
                            ColumnKeys.Type
                        ]?.value.includes(t.id)}
                    />
                    {t.name}
                </div>
            ),
        }));
        const label =
            ids.length > 1
                ? `${ids.length} types`
                : filterStore.taskTypesForCurrentProject.find(
                      (t) => t.id === ids[0],
                  )?.name;
        return dropdownMenu(items, label!);
    };

    const getStatusMenu = (ids: string[]) => {
        const items = filterStore.taskStatusesForCurrentProject.map((t) => {
            const taskType = filterStore.taskTypesForCurrentProject.find(
                (type) => type.id === t.typeId,
            )!.name;
            return {
                key: t.id,
                label: (
                    <div
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent the menu from closing
                            filterStore.setCurrentFilterValue(
                                ColumnKeys.Status,
                                t.id,
                                false,
                            );
                        }}
                        data-testid={`task-status-${t.id}-type-${taskType}`}
                    >
                        <Checkbox
                            style={{ marginRight: 10 }}
                            checked={filterStore.columnsFilterDict[
                                ColumnKeys.Status
                            ]?.value.includes(t.id)}
                        />
                        {`${t.name} (${taskType})`}
                    </div>
                ),
            };
        });
        const label =
            ids.length > 1
                ? `${ids.length} statuses`
                : filterStore.taskStatusesForCurrentProject.find(
                      (t) => t.id === ids[0],
                  )?.name;
        return dropdownMenu(items, label!);
    };

    const getPriorityMenu = (ids: string[]) => {
        const priorities = [
            { key: "LOW", label: "Low" },
            { key: "MEDIUM", label: "Medium" },
            { key: "HIGH", label: "High" },
        ];

        const items = priorities.map((t) => ({
            key: t.key,
            label: (
                <div
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent the menu from closing
                        filterStore.setCurrentFilterValue(
                            ColumnKeys.Priority,
                            t.key,
                            false,
                        );
                    }}
                    data-testid={`task-priority-${t.key}`}
                >
                    <Checkbox
                        style={{ marginRight: 10 }}
                        checked={filterStore.columnsFilterDict[
                            ColumnKeys.Priority
                        ]?.value.includes(t.key)}
                    />
                    {t.label}
                </div>
            ),
        }));

        const label =
            ids.length > 1
                ? `${ids.length} priorities`
                : priorities.find((t) => t.key === ids[0])?.label;
        return dropdownMenu(items, label!);
    };

    const getUsersMenu = (ids: string[], columnName: string) => {
        const items = Object.entries(filterStore.allUsersFullNameResolver)
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map((u) => ({
                key: u[0],
                label: (
                    <div
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent the menu from closing
                            filterStore.setCurrentFilterValue(
                                columnName,
                                u[0],
                                false,
                            );
                        }}
                        data-testid={`task-${columnName}-${u[0]}`}
                    >
                        <Checkbox
                            style={{ marginRight: 10 }}
                            checked={filterStore.columnsFilterDict[
                                columnName
                            ]?.value.includes(u[0])}
                        />
                        {u[1]}
                    </div>
                ),
            }));
        const label =
            ids.length > 1
                ? `${ids.length} ${columnName === ColumnKeys.Owner ? "owners" : "assignees"}`
                : Object.entries(filterStore.allUsersFullNameResolver).find(
                      (u) => u[0] === ids[0],
                  )?.[1];
        return dropdownMenu(items, label!);
    };

    const getDateMenu = (columnName: string, isMetadata: boolean) => {
        const extraFilters =
            filterStore.columnsFilterDict[columnName].extraFilters;
        let label = "";
        if (extraFilters?.extraDateFilter) {
            const dateFilter = extraFilters?.extraDateFilter;
            if (columnName === ColumnKeys.DateCreated || isMetadata) {
                switch (dateFilter.periodCount) {
                    case 1:
                        label = "1 day";
                        break;
                    case 3:
                        label = "3 days";
                        break;
                    case 7:
                        label = "1 week";
                        break;
                    case 30:
                        label = "1 month";
                        break;
                }
            } else if (columnName === ColumnKeys.DueDate) {
                switch (dateFilter.periodCount) {
                    case 0:
                        label = "overdue";
                        break;
                    case 1:
                        label = "1 day";
                        break;
                    case 3:
                        label = "3 days";
                        break;
                    case 7:
                        label = "1 week";
                        break;
                    case 30:
                        label = "1 month";
                        break;
                    default:
                        label = "no due date";
                }
            }
        } else if (columnName === ColumnKeys.DueDate) {
            label = "no due date";
        }

        const handleDateClick = (daysAgo: number) => {
            const extraDateFilter = {
                periodValue: "D",
                periodCount: daysAgo,
            } as ExtraDateFilter;
            filterStore.setDateFilterValue(
                columnName,
                extraDateFilter,
                isMetadata,
            );
        };

        const basicItems = [
            {
                key: `${columnName}oneDay`,
                label: <div onClick={() => handleDateClick(1)}>1 day</div>,
            },
            {
                key: `${columnName}threeDays`,
                label: <div onClick={() => handleDateClick(3)}>3 days</div>,
            },
            {
                key: `${columnName}oneWeek`,
                label: <div onClick={() => handleDateClick(7)}>1 week</div>,
            },
            {
                key: `${columnName}oneMonth`,
                label: <div onClick={() => handleDateClick(30)}>1 month</div>,
            },
        ];

        const dates =
            columnName === ColumnKeys.DueDate
                ? [
                      {
                          key: `${columnName}`,
                          label: (
                              <div onClick={() => handleDateClick(0)}>
                                  overdue
                              </div>
                          ),
                      },
                      ...basicItems,
                      {
                          key: `${columnName}noDueDate`,
                          label: (
                              <div
                                  onClick={() =>
                                      filterStore.setDateFilterValue(
                                          columnName,
                                          null,
                                          false,
                                      )
                                  }
                              >
                                  no due date
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}customDates`,
                          label: (
                              <div
                                  onClick={() =>
                                      filterStore.addCustomDateFilter(
                                          columnName,
                                          isMetadata,
                                      )
                                  }
                              >
                                  Custom dates...
                              </div>
                          ),
                      },
                  ]
                : [
                      ...basicItems,
                      {
                          key: `${columnName}customDates`,
                          label: (
                              <div
                                  onClick={() =>
                                      filterStore.addCustomDateFilter(
                                          columnName,
                                          isMetadata,
                                      )
                                  }
                              >
                                  Custom dates...
                              </div>
                          ),
                      },
                  ];

        const items = dates.map((d) => ({
            key: d.key,
            label: d.label,
        }));

        return dropdownMenu(items, label);
    };

    const getDatePicker = (
        columnName: string,
        isMetadata: boolean,
        isWidgetFilter: boolean,
    ) => {
        return (
            <RangePicker
                disabled={isWidgetFilter}
                defaultOpen={
                    filterStore.columnsFilterDict[columnName]?.value.length ===
                    0
                }
                format={Utils.getDateFormat()}
                className="custom-range-picker"
                value={
                    filterStore.columnsFilterDict[columnName]?.value.map((m) =>
                        dayjs(new Date(m)),
                    ) as never
                }
                onChange={(val) =>
                    filterStore.setCurrentFilterValue(
                        columnName,
                        val?.map((v) => v?.hour(0).minute(0).second(0)) || [],
                        isMetadata,
                    )
                }
            />
        );
    };

    const getMenuFor = (key: string, value: string[]) => {
        const filter = filterStore.columnsFilterDict[key];
        switch (key) {
            case "taskType":
                return getTaskTypeMenu(filter?.value || []);
            case "status":
                return getStatusMenu(filter?.value || []);
            case "priority":
                return getPriorityMenu(filter?.value || []);

            case "assignedTo":
            case "createdBy":
                return getUsersMenu(filter?.value || [], key);
            case "createDate":
            case "dueDate":
                return filter?.value.length || filter?.customFilter
                    ? getDatePicker(key, false, filter.isWidgetFilter)
                    : getDateMenu(key, false);

            default:
                return value;
        }
    };

    const getAttachmentsFilter = (value: unknown, isWidgetFilter: boolean) => {
        const items = [
            {
                key: "has",
                label: "has",
                onClick: () => {
                    filterStore.setCurrentFilterValue(
                        ColumnKeys.Attachments,
                        1,
                        false,
                    );
                },
            },
            {
                key: "hasNo",
                label: "has no",
                onClick: () => {
                    filterStore.setCurrentFilterValue(
                        ColumnKeys.Attachments,
                        0,
                        false,
                    );
                },
            },
        ];
        return (
            <span
                key={ColumnKeys.Attachments}
                className={isWidgetFilter ? "widget-filter" : ""}
            >
                <Tag className="tag first-tag">Task</Tag>
                {dropdownMenu(items, value === 1 ? "has" : "has no")}
                <Tag className="tag not-rounded-bordered">attachments</Tag>
                <Tag
                    className="tag last-tag"
                    icon={<CloseCircleOutlined />}
                    onClick={() =>
                        filterStore.resetColumnFilter(ColumnKeys.Attachments)
                    }
                ></Tag>
            </span>
        );
    };

    const getMetadataChangesFilter = (
        value: unknown,
        isWidgetFilter: boolean,
    ) => {
        const items = [
            {
                key: "has",
                label: "has",
                onClick: () => {
                    filterStore.setCurrentFilterValue(
                        METADATA_CHANGES_FILTER_KEY,
                        1,
                        false,
                    );
                },
            },
            {
                key: "hasNo",
                label: "has no",
                onClick: () => {
                    filterStore.setCurrentFilterValue(
                        METADATA_CHANGES_FILTER_KEY,
                        0,
                        false,
                    );
                },
            },
        ];
        return (
            <span
                key={METADATA_CHANGES_FILTER_KEY}
                className={isWidgetFilter ? "widget-filter" : ""}
            >
                <Tag className="tag first-tag">Task</Tag>
                {dropdownMenu(items, value === 1 ? "has" : "has no")}
                <Tag className="tag not-rounded-bordered">metadata changes</Tag>
                <Tag
                    className="tag last-tag"
                    icon={<CloseCircleOutlined />}
                    onClick={() =>
                        filterStore.resetColumnFilter(
                            METADATA_CHANGES_FILTER_KEY,
                        )
                    }
                ></Tag>
            </span>
        );
    };

    const getCustomFilter = (
        value: unknown,
        columnKey: string,
        isWidgetFilter: boolean,
    ) => {
        const items = [
            {
                key: "is",
                label: "is",
                onClick: () => {
                    filterStore.setCurrentFilterValue(columnKey, true, false);
                },
            },
            {
                key: "isNot",
                label: "is not",
                onClick: () => {
                    filterStore.setCurrentFilterValue(columnKey, false, false);
                },
            },
        ];
        return (
            <span
                key={columnKey}
                className={isWidgetFilter ? "widget-filter" : ""}
            >
                <Tag className="tag first-tag">Task</Tag>
                {dropdownMenu(items, value === true ? "is" : "is not")}
                <Tag className="tag not-rounded-bordered">
                    {columnKey === ColumnKeys.SubTasks
                        ? "subtask"
                        : "completed"}
                </Tag>
                <Tag
                    className="tag last-tag"
                    icon={<CloseCircleOutlined />}
                    onClick={() => filterStore.resetColumnFilter(columnKey)}
                ></Tag>
            </span>
        );
    };

    const getFilterOperation = (key: string) => {
        let items = [] as ItemType[];
        const operator = filterStore.columnsFilterDict[key]!.operator;
        let label = "is";
        switch (key) {
            case ColumnKeys.Status:
            case ColumnKeys.Type:
            case ColumnKeys.Priority:
            case ColumnKeys.AssignedTo:
            case ColumnKeys.Owner:
                label =
                    operator === FilterOperator.Equal ? "is any of" : "is not";
                items = [
                    {
                        key: "isNot",
                        label: "is not",
                        onClick: () => {
                            filterStore.changeFilterOperator(
                                key,
                                FilterOperator.NotEqual,
                            );
                        },
                    },
                    {
                        key: "isAnyOf",
                        label: "is any of",
                        onClick: () => {
                            filterStore.changeFilterOperator(
                                key,
                                FilterOperator.Equal,
                            );
                        },
                    },
                ];
                break;
            case ColumnKeys.DateCreated:
            case ColumnKeys.DueDate:
                return (
                    <Tag
                        className="tag not-rounded-bordered"
                        style={{ borderRight: "none" }}
                    >
                        is
                    </Tag>
                );
        }

        return dropdownMenu(items, label);
    };

    const getMetadataFilterOperation = (
        key: string,
        fieldType?: MetadataFieldType,
    ) => {
        let items = [] as ItemType[];
        const operator = filterStore.columnsFilterDict[key]!.operator;
        let label = "";
        switch (fieldType) {
            case "Text":
                switch (operator) {
                    case FilterOperator.Equal:
                        label = "contains";
                        break;
                    case FilterOperator.NotEqual:
                        label = "does not contain";
                        break;
                    default:
                        label = "is empty";
                        break;
                }
                items = [
                    {
                        key: "1",
                        label: "contains",
                        onClick: () => {
                            filterStore.changeFilterOperator(
                                key,
                                FilterOperator.Equal,
                            );
                        },
                    },
                    {
                        key: "2",
                        label: "does not contain",
                        onClick: () => {
                            filterStore.changeFilterOperator(
                                key,
                                FilterOperator.NotEqual,
                            );
                        },
                    },
                    {
                        key: "3",
                        label: "is empty",
                        onClick: () => {
                            filterStore.changeFilterOperator(
                                key,
                                FilterOperator.Empty,
                            );
                        },
                    },
                ];
                return dropdownMenu(items, label);
            case "Boolean":
            case "DateTime":
                items = [
                    {
                        key: "1",
                        label: "is",
                        onClick: () => {
                            filterStore.changeFilterOperator(
                                key,
                                FilterOperator.Equal,
                            );
                        },
                    },
                    {
                        key: "2",
                        label: "is empty",
                        onClick: () => {
                            filterStore.changeFilterOperator(
                                key,
                                FilterOperator.Empty,
                            );
                        },
                    },
                ];
                label = operator === FilterOperator.Equal ? "is" : "is empty";
                return dropdownMenu(items, label);
            case "Number":
                label =
                    NumericFilterOptions[
                        operator as keyof typeof NumericFilterOptions
                    ];
                items = Object.keys(NumericFilterOptions).map((k) => ({
                    key: k,
                    label: NumericFilterOptions[k],
                    onClick: () => {
                        filterStore.changeFilterOperator(key, k);
                    },
                }));
                return dropdownMenu(items, label);

            default:
                return <></>;
        }
    };

    const getMetadataMenu = (
        value: unknown[],
        metadata?: MetadataDefinition,
    ) => {
        if (metadata?.name) {
            const operator =
                filterStore.columnsFilterDict[metadata.id]!.operator;
            if (operator === FilterOperator.Empty) {
                return <Tag className="tag not-rounded-bordered ">-</Tag>;
            }
        }
        let label = value.length > 1 ? `${value.length} items` : value[0];
        if (metadata?.fieldType === "Boolean") {
            const { trueVal, falseVal } = metadata.format
                ? JSON.parse(metadata.format)
                : { trueVal: "true", falseVal: "false" };
            label = value[0] === true ? trueVal || "true" : falseVal || "false";
        }
        if (metadata?.fieldType === "DateTime") {
            return getDateMenu(metadata.id, true);
        }
        const items = [
            {
                key: 1,
                label: (
                    <MetadataFilterControls
                        filterStore={filterStore}
                        metadata={metadata}
                        isAppliedFilter
                    />
                ),
            },
        ];
        return dropdownMenu(items, label as string, "metadata-filter-menu");
    };

    return (
        <Row>
            <div className="composed-filters-container">
                {Object.keys(filterStore.columnsFilterDict).map((k) => {
                    const filter = filterStore.columnsFilterDict[k];
                    const value = filter.value;
                    if (filter.fieldType === "Metadata") {
                        const metadata = taskGridVisualStore.tableMetaData.find(
                            (m) => m.id === filter.entityReference,
                        );
                        if (!metadata) {
                            return null;
                        }
                        // Skip filters that are only widget specific;
                        if (
                            (!value.length || !value[0]) &&
                            metadata?.fieldType === "Number"
                        ) {
                            return null;
                        }
                        return (
                            <span
                                key={k}
                                className={
                                    filter.isWidgetFilter ? "widget-filter" : ""
                                }
                            >
                                <Tag className="tag first-tag">
                                    {metadata?.title}
                                </Tag>
                                {getMetadataFilterOperation(
                                    k,
                                    metadata?.fieldType,
                                )}
                                {metadata?.fieldType === "DateTime" &&
                                (filter.customFilter || filter?.value.length)
                                    ? getDatePicker(
                                          metadata!.id,
                                          true,
                                          filter.isWidgetFilter,
                                      )
                                    : getMetadataMenu(value, metadata)}
                                <Tag
                                    className="tag last-tag"
                                    icon={<CloseCircleOutlined />}
                                    onClick={() =>
                                        filterStore.resetColumnFilter(k)
                                    }
                                ></Tag>
                            </span>
                        );
                    }
                    switch (k) {
                        case ColumnKeys.Attachments:
                            return getAttachmentsFilter(
                                value[0],
                                filter.isWidgetFilter,
                            );
                        case METADATA_CHANGES_FILTER_KEY:
                            return getMetadataChangesFilter(
                                value[0],
                                filter.isWidgetFilter,
                            );
                        case ColumnKeys.SubTasks:
                            return getCustomFilter(
                                value[0],
                                ColumnKeys.SubTasks,
                                filter.isWidgetFilter,
                            );
                        case ColumnKeys.DoneTasks:
                            return getCustomFilter(
                                value[0],
                                ColumnKeys.DoneTasks,
                                filter.isWidgetFilter,
                            );
                        default:
                            return (
                                <span
                                    key={k}
                                    className={
                                        filter.isWidgetFilter
                                            ? "widget-filter"
                                            : ""
                                    }
                                >
                                    <Tag className="tag first-tag">
                                        {LabelMapper[k]}
                                    </Tag>
                                    {getFilterOperation(k)}
                                    {getMenuFor(k, value)}
                                    <Tag
                                        className="tag last-tag"
                                        icon={<CloseCircleOutlined />}
                                        onClick={() =>
                                            filterStore.resetColumnFilter(k)
                                        }
                                    ></Tag>
                                </span>
                            );
                    }
                })}
                {!filterStore.isWidgetView &&
                    Object.keys(filterStore.columnsFilterDict).length > 1 && (
                        <Button
                            className="reset-button"
                            type="default"
                            icon={<CloseCircleOutlined />}
                            onClick={filterStore.handleAllFiltersReset}
                        >
                            Clear all
                        </Button>
                    )}
            </div>
        </Row>
    );
};

export default observer(ComposedTableFilter);
